import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Clapperboard, Loader2, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { FilmSlateLoader } from "@/components/FilmSlateLoader";

const HERO_BG =
  "https://images.pexels.com/photos/33419101/pexels-photo-33419101.jpeg";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    if (!agreed) {
      toast.error("Please agree to the Terms, Privacy Policy, and Copyright Policy first");
      return;
    }
    setRedirecting(true);
    const redirectUrl = window.location.origin + "/dashboard";
    setTimeout(() => {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 1100);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to the Terms, Privacy Policy, and Copyright Policy first");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, agreed);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505]">
      {redirecting && <FilmSlateLoader message="Opening secure sign-in…" />}
      <div className="flex items-center justify-center p-6 md:p-12 order-2 lg:order-1">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#00F0FF] text-black">
              <Clapperboard className="h-5 w-5" />
            </span>
            <span className="font-display text-xl font-bold">
              Narrative<span className="text-[#00F0FF]">Vault</span>
            </span>
          </Link>

          <h1 className="font-display text-3xl font-bold">Create your studio</h1>
          <p className="text-white/50 mt-2 text-sm">Start monetizing your event footage today.</p>

          <button
            onClick={googleLogin}
            data-testid="google-register-btn"
            className="mt-8 w-full flex items-center justify-center gap-3 bg-white text-black font-medium rounded-full py-3 hover:scale-[1.02] transition-transform duration-300"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-white/40">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="register-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (min 6 chars)"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors"
              />
            </div>
            <label className="flex items-start gap-3 text-xs text-white/60 leading-relaxed cursor-pointer select-none">
              <input
                data-testid="register-agree-checkbox"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-white/5 accent-[#00F0FF] cursor-pointer"
              />
              <span>
                I certify that I have the legal right to upload and share all content I submit to
                Narrative Vault, and I agree to the{" "}
                <Link to="/terms" target="_blank" className="text-[#00F0FF] hover:underline">Terms of Service</Link>,{" "}
                <Link to="/privacy" target="_blank" className="text-[#00F0FF] hover:underline">Privacy Policy</Link>, and{" "}
                <Link to="/copyright" target="_blank" className="text-[#00F0FF] hover:underline">Copyright Policy</Link>.
              </span>
            </label>
            <button
              data-testid="register-submit"
              disabled={loading || !agreed}
              className="w-full bg-[#00F0FF] text-black font-semibold rounded-full py-3 hover:scale-[1.02] transition-transform duration-300 disabled:opacity-40 disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Account
            </button>
          </form>

          <p className="text-sm text-white/50 mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-[#00F0FF] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block grain overflow-hidden order-1 lg:order-2">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
        <div className="relative z-10 h-full flex flex-col justify-end p-14">
          <span className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-3">Narrative Vault</span>
          <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
            Your best clips deserve a premiere.
          </h2>
          <p className="text-white/60 mt-4 max-w-sm">
            Upload once, share a private Narrative Vault link, and let clients unlock the shots they love.
          </p>
        </div>
      </div>
    </div>
  );
}
